import ImageLayer from 'ol/layer/Image';
import ImageStatic from 'ol/source/ImageStatic';
import { useCallback, useEffect, useRef } from 'react';
import { useElement, useLayers } from '@ais-ui/react-openlayers';

interface CoverageImageWMSLayerProps {
  overlay: string | null;
  extent: number[]; // [minX, minY, maxX, maxY]
  name: string;
  properties: Record<string, any>;
}

export function CoverageImageWMSLayer({
  overlay,
  extent,
  name,
  properties,
}: CoverageImageWMSLayerProps) {
  const prevOverlay = useRef<string | null>(null);
  const prevExtent = useRef<number[] | null>(null);
  const { removeLayer } = useLayers();

  const createLayer = useCallback(() => {
    return new ImageLayer({
      source: new ImageStatic({
        url: `data:image/png;base64,${overlay}`,
        imageExtent: extent,
      }),
    });
  }, [overlay, extent]);

  const elementRef = useRef<ImageLayer<ImageStatic>>(createLayer());

  useEffect(() => {
    if (overlay === prevOverlay.current && extent === prevExtent.current) {
      return;
    }

    prevOverlay.current = overlay;
    prevExtent.current = extent;

    // Create and replace the layer
    const newLayer = createLayer();

    if (elementRef.current) {
      removeLayer(elementRef.current);
    }
    elementRef.current = newLayer;

    return () => {
      // Cleanup the layer when component unmounts or layer is replaced
      if (elementRef.current) {
        removeLayer(elementRef.current);
      }
    };
  }, [overlay, extent, createLayer, removeLayer]);

  const canZoomToFromLayerControl = true;
  const isRemovableFromLayerControl = true;

  useElement(
    elementRef.current,
    name,
    canZoomToFromLayerControl,
    isRemovableFromLayerControl,
    properties
  );

  return null;
}
